import { useMutation } from '@apollo/client';
import type { InsertDocumentAssessmentResultMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { InsertDocumentAssessmentResultDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import type { InsertDocumentAssessmentResultTRPCInput } from './useInsertDocumentAssessmentResultTRPC';
import { useInsertDocumentAssessmentResultTRPC } from './useInsertDocumentAssessmentResultTRPC';

export const useInsertDocumentAssessmentResult = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // GraphQL mutation
  const [insertDocumentAssessmentResultGraphQL, graphqlState] = useMutation(
    InsertDocumentAssessmentResultDocument,
    {
      update: (cache) => {
        evictField(cache, 'document_assessment_result');
        evictField(cache, 'assessment');
        evictField(cache, 'internal_audit_report');
        evictField(cache, 'oversight_assessment');
        evictField(cache, 'document_assessment_result_aggregate');
      },
    }
  );

  // tRPC mutation
  const trpcMutation = useInsertDocumentAssessmentResultTRPC();

  const insertDocumentAssessmentResult = async (
    variables: InsertDocumentAssessmentResultTRPCInput
  ): Promise<InsertDocumentAssessmentResultMutation> => {
    if (trpcEnabled) {
      return trpcMutation.insertDocumentAssessmentResult(variables);
    }

    const result = await insertDocumentAssessmentResultGraphQL({
      variables,
    });
    if (!result.data) {
      throw new Error('Failed to insert document assessment result');
    }

    return result.data;
  };

  // Return appropriate state based on feature flag
  if (trpcEnabled) {
    return {
      insertDocumentAssessmentResult,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    insertDocumentAssessmentResult,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
